'use client';

import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { createBrowserClient } from '@/lib/supabase-clients';
import { Session, User } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { logPhoneFormat, isValidE164 } from '@/lib/utils/phone-validation';

// Simple interface for user profile
export interface UserProfile {
  id: string;
  auth_id: string;
  name: string | null;
  email: string | null;
  phone?: string | null;
  phone_verified?: boolean;
  handle: string | null;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
  domain_id?: string | null;
  active_domain_id?: string | null; // CRITICAL: User's currently active domain
  active_domain_handle?: string | null;
  active_domain_role?: string | null;
}

// Authentication status
type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

// Auth context interface
interface SupabaseAuthContextType {
  // Auth state
  isAuthenticated: boolean;
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  status: AuthStatus;
  error: string | null;
  isLoading: boolean;
  
  // Auth functions
  signInWithEmail: (email: string) => Promise<{ success: boolean; error: string | null; needsPhone?: boolean; maskedPhone?: string; fullPhone?: string }>;
  signInWithPhone: (phone: string, email?: string) => Promise<{ success: boolean; error: string | null; fallbackUsed?: boolean }>;
  signInWithOAuth: (provider: 'azure') => Promise<{ success: boolean; error: string | null }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<Session | null>;
  
  // Profile functions
  refreshProfile: () => Promise<UserProfile | null>;
  
  // Backward compatibility
  isUsingImprovedAuth?: boolean;
  authStatus?: AuthStatus;
}

// Create the context
const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(undefined);

// Auth provider component
export function SupabaseAuthProvider({ children }: { children: ReactNode }) {
  // Use the singleton Supabase client
  const supabase = createBrowserClient();
  const router = useRouter();
  
  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initTimeout, setInitTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const lastRefreshTime = useRef<number>(0);
  
  // Compute authentication status
  const isAuthenticated = !!user && status === 'authenticated';
  
  // Profile persistence ref
  const profileRef = useRef<UserProfile | null>(null);
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);
  
  // Fetch user profile with timeout
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    console.log('[UserProfileService] fetchProfile called with userId:', userId);
    
    // Create a timeout promise
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => {
        console.warn('[UserProfileService] Profile fetch timed out after 5 seconds');
        resolve(null);
      }, 5000); // 5 second timeout
    });
    
    // Create the fetch promise
    const fetchPromise = (async () => {
      try {
        console.log('[UserProfileService] Starting profile query...');
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', userId)
          .maybeSingle(); // Use maybeSingle() instead of single() to handle 0 or 1 rows gracefully
          
        console.log('[UserProfileService] Query result:', { data: !!data, error: !!error, errorMessage: error?.message });
          
        if (error) {
          console.error('[UserProfileService] Error fetching user profile:', error);
          return null;
        }
        
        if (!data) {
          console.warn('[UserProfileService] No profile found for user:', userId);
          return null;
        }
        
        console.log('[UserProfileService] Profile found:', data);
        const userProfile = data as UserProfile;

        // Enrich with active domain handle and role for team context
        if (userProfile.active_domain_id && userProfile.active_domain_id !== userProfile.domain_id) {
          try {
            const [domainResult, memberResult] = await Promise.all([
              supabase.from('domains').select('handle').eq('id', userProfile.active_domain_id).single(),
              supabase.from('domain_members').select('role').eq('domain_id', userProfile.active_domain_id).eq('user_id', userProfile.id).single(),
            ]);
            userProfile.active_domain_handle = domainResult.data?.handle || null;
            userProfile.active_domain_role = memberResult.data?.role || null;
          } catch {
            // Non-blocking — fall back to personal room
          }
        }

        return userProfile;
      } catch (error) {
        console.error('[UserProfileService] Unexpected error in fetchProfile:', error);
        return null;
      }
    })();
    
    // Race between fetch and timeout
    return Promise.race([fetchPromise, timeoutPromise]);
  }, [supabase]);
  
  // Refresh user profile
  const refreshProfile = useCallback(async (): Promise<UserProfile | null> => {
    console.log('[Auth] refreshProfile called, user:', user?.id);
    if (!user) {
      console.log('[Auth] refreshProfile: No user, returning null');
      return null;
    }
    
    try {
      console.log('[Auth] refreshProfile: Fetching profile for user:', user.id);
      const userProfile = await fetchProfile(user.id);
      console.log('[Auth] refreshProfile: Fetched profile:', userProfile);
      
      if (userProfile) {
        console.log('[Auth] refreshProfile: Setting profile in state');
        setProfile(userProfile);
      } else {
        console.log('[Auth] refreshProfile: No profile returned from fetchProfile');
      }
      return userProfile;
    } catch (error) {
      console.error('[Auth] refreshProfile: Error:', error);
      return null;
    }
  }, [user, fetchProfile]);
  
  // Sign in with email - APPROACH 1: Email-First (No Phone Required)
  const signInWithEmail = useCallback(async (email: string): Promise<{ success: boolean; error: string | null; needsPhone?: boolean; maskedPhone?: string; fullPhone?: string }> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('[Auth][Approach1] Email-only signin for:', email);

      // APPROACH 1: Always send EMAIL OTP
      // No phone checking, no SMS during signup
      // Phone is added AFTER login via settings or admin
      const { error: emailError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/api/auth/callback`,
          shouldCreateUser: true,  // Create email-based user
        }
      });

      if (emailError) {
        console.error('[Auth][Approach1] Email OTP failed:', emailError.message);
        setError(emailError.message);
        return { success: false, error: emailError.message };
      }

      console.log('[Auth][Approach1] Email OTP sent successfully');
      return {
        success: true,
        error: null,
        needsPhone: false,  // Never need phone during signup
      };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  // Sign in with phone (SMS OTP) with auto-fallback to email
  const signInWithPhone = useCallback(async (
    phone: string,
    email?: string
  ): Promise<{ success: boolean; error: string | null; fallbackUsed?: boolean }> => {
    try {
      setIsLoading(true);
      setError(null);

      // Preserve current URL parameters
      const currentParams = new URLSearchParams(window.location.search);
      const invite = currentParams.get('invite');
      const next = currentParams.get('next');

      // Build callback URL with preserved parameters
      let callbackUrl = `${window.location.origin}/api/auth/callback`;
      const callbackParams = new URLSearchParams();

      if (invite) {
        callbackParams.set('invite', invite);
      }
      if (next) {
        callbackParams.set('next', next);
      }

      if (callbackParams.toString()) {
        callbackUrl += `?${callbackParams.toString()}`;
      }

      console.log('[Auth] Attempting SMS OTP to phone:', phone);

      // Log phone format before sending OTP (critical for debugging)
      logPhoneFormat('signInWithPhone - before signInWithOtp', phone, {
        email: email,
        hasEmail: !!email,
        callbackUrl: callbackUrl,
      });

      // Validate phone format before sending
      if (!isValidE164(phone)) {
        console.error('[Auth] Phone is not valid E.164 format:', phone);
        setError('Invalid phone format. Phone must be in E.164 format (e.g., +18676896780)');
        return {
          success: false,
          error: 'Invalid phone format. Please enter phone with country code (e.g., +1 for US/Canada)',
          fallbackUsed: false
        };
      }

      // Try SMS OTP first
      const { error: smsError } = await supabase.auth.signInWithOtp({
        phone,
        options: {
          // Allow creating user only when adding NEW phone number
          // For existing users, phone should already exist in auth.users
          shouldCreateUser: true,
          data: email ? {
            email,
            notification_email: email
          } : undefined
        }
      });

      // If SMS succeeds, return success
      if (!smsError) {
        console.log('[Auth] SMS OTP sent successfully');
        return { success: true, error: null, fallbackUsed: false };
      }

      console.error('[Auth] SMS OTP failed:', smsError.message);

      // Auto-fallback to email OTP if SMS fails and email is provided
      if (email) {
        console.log('[Auth] Falling back to email OTP for:', email);

        const { error: emailError } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: callbackUrl,
            shouldCreateUser: true,
            data: {
              phone,
              fallback_from_sms: true
            }
          }
        });

        if (emailError) {
          console.error('[Auth] Email fallback also failed:', emailError.message);
          setError(emailError.message);
          return {
            success: false,
            error: `SMS failed: ${smsError.message}. Email fallback also failed: ${emailError.message}`,
            fallbackUsed: true
          };
        }

        console.log('[Auth] Email OTP fallback sent successfully');
        return {
          success: true,
          error: null,
          fallbackUsed: true
        };
      }

      // No email provided for fallback
      setError(smsError.message);
      return {
        success: false,
        error: `SMS OTP failed: ${smsError.message}. No email provided for fallback.`,
        fallbackUsed: false
      };

    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in';
      setError(errorMessage);
      return { success: false, error: errorMessage, fallbackUsed: false };
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  // Sign in with OAuth provider (e.g., Microsoft/Azure)
  const signInWithOAuth = useCallback(async (provider: 'azure'): Promise<{ success: boolean; error: string | null }> => {
    try {
      setIsLoading(true);
      setError(null);

      const currentParams = new URLSearchParams(window.location.search);
      const invite = currentParams.get('invite');
      const next = currentParams.get('next');
      const redirect = currentParams.get('redirect');

      let callbackUrl = `${window.location.origin}/api/auth/callback`;
      const callbackParams = new URLSearchParams();

      if (invite) callbackParams.set('invite', invite);
      if (next) callbackParams.set('next', next);
      if (redirect) callbackParams.set('redirect', redirect);

      if (callbackParams.toString()) {
        callbackUrl += `?${callbackParams.toString()}`;
      }

      console.log('[Auth] Starting OAuth sign-in with provider:', provider, 'redirectTo:', callbackUrl);

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl,
          scopes: 'email',
        },
      });

      if (oauthError) {
        console.error('[Auth] OAuth sign-in error:', oauthError.message);
        setError(oauthError.message);
        return { success: false, error: oauthError.message };
      }

      return { success: true, error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in with OAuth';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    console.log('[Auth] SignOut called');
    try {
      setIsLoading(true);
      
      // Use the logout API route for better cookie handling
      console.log('[Auth] Calling logout API');
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Logout failed');
      }
      
      const data = await response.json();
      console.log('[Auth] Logout API response:', data);
      
      // Clear local state
      console.log('[Auth] Clearing local state');
      setUser(null);
      setSession(null);
      setProfile(null);
      setStatus('unauthenticated');
      
      // Clear any persisted auth data
      if (typeof window !== 'undefined') {
        const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
        localStorage.removeItem(`sb-${projectRef}-auth-token`);
        localStorage.removeItem('supabase.auth.token');
      }
      
      // Redirect to login
      console.log('[Auth] Redirecting to /login');
      router.push('/login');
    } catch (error) {
      console.error('[Auth] Error signing out:', error);
      
      // Even if logout fails, clear local state and redirect
      setUser(null);
      setSession(null);
      setProfile(null);
      setStatus('unauthenticated');
      router.push('/login');
    } finally {
      setIsLoading(false);
    }
  }, [router]);
  
  // Refresh session
  const refreshSession = useCallback(async (options?: { silent?: boolean }): Promise<Session | null> => {
    try {
      console.log('[Auth] Refreshing session', options?.silent ? '(silent)' : '');
      
      // Store the current profile before refreshing
      const currentProfile = profile;
      const currentUserId = user?.id;
      
      // Set loading state only if not a silent refresh
      if (!options?.silent) {
        setIsLoading(true);
      }
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Error refreshing session:', error);
        if (!options?.silent) {
          setIsLoading(false);
        }
        return null;
      }
      
      if (data?.session) {
        console.log('[Auth] Session refreshed successfully');
        
        // IMPORTANT: First update session and user
        // TypeScript safety check (data.session is guaranteed to be non-null here)
        const session = data.session!;
        setSession(session);
        setUser(session.user);
        setStatus('authenticated'); // Ensure we're properly authenticated after refresh
        
        // Preserve the profile during the refresh if user ID matches
        // This is critical to prevent the profile from disappearing in the header
        if (currentProfile && currentProfile.auth_id === data.session.user.id) {
          console.log('[Auth] Preserving existing profile during refresh');
          // Explicitly set profile again to ensure it's still in state
          setProfile(currentProfile);
          
          // Add a small delay before fetching fresh profile to allow UI to stabilize
          setTimeout(async () => {
            try {
              // Ensure session and user are still valid before fetching profile
              if (!data.session || !data.session.user) {
                console.warn('[Auth] No valid session/user found during background profile refresh');
                return;
              }
              // Quietly refresh profile in the background without causing UI flicker
              console.log('[Auth] Background refreshing profile data');
              const userProfile = await fetchProfile(data.session.user.id);
              if (userProfile) {
                // Only update if the profile has actually changed
                if (JSON.stringify(userProfile) !== JSON.stringify(currentProfile)) {
                  console.log('[Auth] Profile updated with fresh data');
                  setProfile(userProfile);
                } else {
                  console.log('[Auth] Profile unchanged, preserving current data');
                }
              }
            } catch (profileError) {
              console.error('[Auth] Error in background profile refresh:', profileError);
              // Keep existing profile on error
            }
          }, 500);
        } else {
          // No existing profile or user ID changed - fetch a fresh profile
          console.log('[Auth] Fetching fresh profile after session refresh');
          try {
            const userProfile = await fetchProfile(data.session.user.id);
            if (userProfile) {
              setProfile(userProfile);
            }
          } catch (profileError) {
            console.error('[Auth] Error fetching profile during session refresh:', profileError);
            
            // If we had a profile before but failed to fetch a new one,
            // preserve the old one if we're still looking at the same user
            if (currentProfile && currentUserId === data.session.user.id) {
              console.log('[Auth] Restoring previous profile after fetch failure');
              setProfile(currentProfile);
            }
          }
        }
        
        if (!options?.silent) {
          setIsLoading(false);
        }
        return data.session;
      }
      
      if (!options?.silent) {
        setIsLoading(false);
      }
      return null;
    } catch (error) {
      console.error('Error in refreshSession:', error);
      if (!options?.silent) {
        setIsLoading(false);
      }
      return null;
    }
  }, [supabase, profile, user, fetchProfile]);
  
  // Function to synchronize auth state from localStorage if needed
  const syncAuthFromLocalStorage = useCallback(() => {
    try {
      // Skip if already authenticated, initializing, or currently loading
      if (isAuthenticated || status === ('loading' as AuthStatus) || isLoading || !window.localStorage) {
        return false;
      }

      // Check for auth data in localStorage
      const persistedAuth = localStorage.getItem('supabase.auth.token');
      
      if (persistedAuth) {
        // Found persisted auth data, trigger refresh to validate it
        console.log('[Auth] Found persisted auth data in localStorage, refreshing session');
        // We'll return true but defer the actual refresh to avoid race conditions
        setTimeout(() => {
          if (!isAuthenticated && status !== ('loading' as AuthStatus)) {
            refreshSession();
          }
        }, 100);
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('[Auth] Error syncing from localStorage:', err);
      return false;
    }
  }, [isAuthenticated, status, isLoading, refreshSession]);
  
  // Initialize auth - use a ref to prevent infinite loops
  const initRef = useRef(false);
  
  useEffect(() => {
    let mounted = true;
    let timeout: NodeJS.Timeout | null = null;

    const initializeAuth = async () => {
      setIsLoading(true);
      setStatus('loading');
      setError(null);

      // Small delay to ensure cookies are ready after redirect
      const urlParams = new URLSearchParams(window.location.search);
      const hasLoginSuccess = urlParams.has('login_success');
      
      if (hasLoginSuccess) {
        console.log('[Auth] Detected login success, waiting for cookies to settle...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Try to restore session
      console.log('[Auth] Checking for existing session...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('[Auth] Error getting session:', sessionError);
        // If there's an error getting the session, set states and return early
        if (mounted) {
          setStatus('unauthenticated');
          setUser(null);
          setProfile(null);
          setSession(null);
          setIsLoading(false);
          setIsInitialized(true);
          setError(sessionError.message);
        }
        return;
      } else {
        console.log('[Auth] Session check result:', session ? 'Found session' : 'No session', session?.user?.id);
      }

      if (!mounted) return;

      if (session) {
        console.log('[Auth] Session found during init, setting user:', session.user.id);
        setSession(session);
        setUser(session.user);
        setStatus('authenticated');
        // Fetch profile only after session is ready
        console.log('[Auth] Fetching profile during init for user:', session.user.id);
        const userProfile = await fetchProfile(session.user.id);
        console.log('[Auth] Profile fetch result during init:', userProfile);
        if (mounted) {
          console.log('[Auth] Component is mounted, setting profile:', userProfile);
          setProfile(userProfile);
        } else {
          console.log('[Auth] Component unmounted, skipping profile set');
        }
        setIsLoading(false);
        setIsInitialized(true);
        
        // If we have login_success param and we're authenticated, redirect immediately
        if (hasLoginSuccess && window.location.pathname === '/login') {
          console.log('[Auth] Login success with valid session, redirecting');
          const destination = userProfile ? '/dashboard' : '/onboarding';
          window.location.replace(destination);
        }
        return;
      }

      // Start timeout only after session restoration attempt
      timeout = setTimeout(async () => {
        if (!mounted) return;
        // Fallback: Try to rehydrate session from localStorage/cookies
        const { data: { session: fallbackSession } } = await supabase.auth.getSession();
        if (fallbackSession) {
          setSession(fallbackSession);
          setUser(fallbackSession.user);
          setStatus('authenticated');
          const userProfile = await fetchProfile(fallbackSession.user.id);
          if (mounted) setProfile(userProfile);
          setIsLoading(false);
          setIsInitialized(true);
          return;
        }
        // If still no session, set to unauthenticated
        setStatus('unauthenticated');
        setUser(null);
        setProfile(null);
        setSession(null);
        setIsLoading(false);
        setIsInitialized(true);
        setError('Authentication timed out');
      }, 30000); // 30 seconds
      
      // If no session found immediately, set states properly
      if (!session) {
        console.log('[Auth] No session found, setting unauthenticated state');
        if (mounted) {
          setStatus('unauthenticated');
          setUser(null);
          setProfile(null);
          setSession(null);
          setIsLoading(false);
          setIsInitialized(true);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (timeout) clearTimeout(timeout);
    };
  }, []);
  
  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[Auth] Auth state changed:', event, session?.user?.id);
      
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        console.log('[Auth] Processing', event, 'event');
        console.log('[Auth] Current profile state:', profile);
        
        // Only update state if it's actually changing
        const isNewUser = user?.id !== session.user.id;
        
        setSession(session);
        setUser(session.user);
        setStatus('authenticated');
        setIsLoading(false);
        setIsInitialized(true);
        
        // Only fetch profile if we don't have one or if it's a different user
        if (!profile && !profileRef.current || isNewUser) {
          console.log('[Auth] Fetching profile in onAuthStateChange for:', session.user.id);
          const userProfile = await fetchProfile(session.user.id);
          console.log('[Auth] Profile result in onAuthStateChange:', userProfile);
          if (userProfile) {
            console.log('[Auth] Setting profile from onAuthStateChange');
            setProfile(userProfile);
          } else {
            console.log('[Auth] No profile found in onAuthStateChange, scheduling retry');
            // Retry profile fetch after a delay if it failed
            setTimeout(async () => {
              console.log('[Auth] Retrying profile fetch after delay');
              const retryProfile = await fetchProfile(session.user.id);
              if (retryProfile) {
                console.log('[Auth] Retry successful, setting profile');
                setProfile(retryProfile);
              }
            }, 2000); // Retry after 2 seconds
          }
        } else {
          console.log('[Auth] Profile already exists, preserving it:', profile || profileRef.current);
          // Restore profile from ref if state was lost
          if (!profile && profileRef.current) {
            console.log('[Auth] Restoring profile from ref');
            setProfile(profileRef.current);
          }
        }
        
        // If we're on the login page after sign in, redirect
        if (window.location.pathname === '/login') {
          console.log('[Auth] Signed in on login page, redirecting');
          const redirectTo = profileRef.current ? '/dashboard/moments' : '/onboarding';
          
          // Use a small delay to ensure state is fully updated
          setTimeout(() => {
            window.location.replace(redirectTo);
          }, 100);
        }
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        setStatus('unauthenticated');
        setIsLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        console.log('[Auth] TOKEN_REFRESHED event, preserving profile');
        setSession(session);
        setUser(session.user);
        // IMPORTANT: Do not clear profile on token refresh
      } else if (event === 'USER_UPDATED' && session) {
        setSession(session);
        setUser(session.user);
        // Re-fetch profile on user update
        const userProfile = await fetchProfile(session.user.id);
        if (userProfile) {
          setProfile(userProfile);
        }
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile, supabase.auth]);
  
  // Handle navigation events - sync from localStorage if we lose auth state
  useEffect(() => {
    // Try to sync auth state if we think we're unauthenticated but localStorage says otherwise
    if (status === 'unauthenticated' && !isLoading) {
      syncAuthFromLocalStorage();
    }
    
    // Listen for focus events (when returning to tab)
    const handleFocus = () => {
      // Skip auth refresh if we're in a LiveKit room to prevent disconnection
      const pathname = window.location.pathname;
      const isInRoom = pathname.includes('/rooms/') || pathname.includes('/custom');
      
      if (isInRoom) {
        console.log('[Auth] Skipping auth refresh - active room session');
        return;
      }
      
      // Debounce: Skip if we refreshed less than 30 seconds ago
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTime.current;
      const MIN_REFRESH_INTERVAL = 30000; // 30 seconds
      
      if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
        console.log('[Auth] Skipping refresh - too soon since last refresh', `(${Math.round(timeSinceLastRefresh / 1000)}s ago)`);
        return;
      }
      
      if (isAuthenticated && user) {
        console.log('[Auth] Window focused, verifying auth state');
        lastRefreshTime.current = now;
        
        // Use silent refresh for authenticated users to avoid UI flicker
        refreshSession({ silent: true }).then(session => {
          if (!session && user) {
            console.log('[Auth] Session invalid after focus, refreshing');
            syncAuthFromLocalStorage();
          }
        });
      } else if (!isAuthenticated && status !== 'loading') {
        // Check if we should be authenticated
        syncAuthFromLocalStorage();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, isLoading, refreshSession, status, syncAuthFromLocalStorage, user]);
  
  // Add regular profile refresh for long-running sessions
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;
    
    if (isAuthenticated && user) {
      refreshInterval = setInterval(() => {
        // Skip scheduled refresh if we're in a LiveKit room
        const pathname = window.location.pathname;
        const isInRoom = pathname.includes('/rooms/') || pathname.includes('/custom');
        
        if (isInRoom) {
          console.log('[Auth] Skipping scheduled profile refresh - active room session');
          return;
        }
        
        console.log('[Auth] Running scheduled profile refresh');
        refreshProfile();
      }, 5 * 60 * 1000); // Every 5 minutes
    }
    
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [isAuthenticated, user, refreshProfile]);
  
  const contextValue: SupabaseAuthContextType = {
    isAuthenticated,
    user,
    profile,
    session,
    status,
    error,
    isLoading,
    signInWithEmail,
    signInWithPhone,
    signInWithOAuth,
    signOut,
    refreshSession,
    refreshProfile,
    // Backward compatibility properties
    authStatus: status,
    isUsingImprovedAuth: true
  };
  
  return (
    <SupabaseAuthContext.Provider value={contextValue}>
      {children}
    </SupabaseAuthContext.Provider>
  );
}

// Hook to use the auth context
export function useSupabaseAuth() {
  const context = useContext(SupabaseAuthContext);
  
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  
  return context;
}